import * as React from "react";
import {
  SortingState,
  ColumnFiltersState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogOverlay,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Edit, PenTool, Plus, Trash } from "lucide-react";
import { useAuth } from "@/core/auth/contexts/AuthContext";
import withAuthorization from "@/core/auth/components/withAuthorization";
import { Role } from "@/core/auth/models/role.model";
import RoleGuard from "@/core/auth/components/role-guard";

import { columns } from "./components/columns";
import { fetchCategories } from "@/services/getCategories";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { updateCategory } from "@/services/updateCategories";
import { deleteCategory } from "@/services/deleteCategory";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DotsHorizontalIcon } from "@radix-ui/react-icons";
import { Skeleton } from "@/components/ui/skeleton";

function CategoriesTable() {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const [currentCategory, setCurrentCategory] = React.useState<any>(null);
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const [categoryToDelete, setCategoryToDelete] = React.useState<any>(null);
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const CategoryData = useQuery({
    queryKey: ["categories"],
    queryFn: async () => {
      const data = await fetchCategories();
      return data.map((category: any) => {
        return { ...category, company: category.company?.name };
      });
    },
    enabled: !!user?.company?._id || user?.role === "super-admin",
  });

  const mutation = useMutation({
    mutationFn: (updatedCategory: any) =>
      updateCategory(updatedCategory._id, updatedCategory),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      toast.success("Category updated successfully");

      setIsEditModalOpen(false);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleEditClick = (category: any) => {
    setCurrentCategory(category);
    setIsEditModalOpen(true);
  };

  const deleteMutation = useMutation({
    mutationFn: (categoryId: string) => deleteCategory(categoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      toast.success("Category deleted successfully");
    },
    onError: (error) => {
      toast.error("Error deleting employee: " + error.message);
    },
  });

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (currentCategory) {
      mutation.mutate(currentCategory);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentCategory((prev: any) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDeleteClick = (category: any) => {
    setCategoryToDelete(category);
    setIsDeleteModalOpen(true);
  };

  const table = useReactTable({
    data: Array.isArray(CategoryData.data) ? CategoryData.data : [],
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });
  return (
    <div className="w-full p-4">
      <RoleGuard requiredRoles={["company-admin", "super-admin"]}>
        <Link to="/app/add-category">
          <Button
            variant="outline"
            className="ml-auto flex justify-center w-auto mt-6 gap-2 items-center"
          >
            <Plus className="h-6 w-6" />
            Add Category
          </Button>
        </Link>
      </RoleGuard>
      {CategoryData.isPending ? (
        [1, 2, 3, 4].map((item) => (
              <div key={item} className="flex items-center space-x-4 w-full my-8">
                <div className="space-y-2 w-full">
                  <Skeleton className="h-4 w-[100%]" />
                  <Skeleton className="h-4 w-[100%]" />
                </div>
              </div>
            ))
      ) : CategoryData.isError ? (
        <div>Error: {CategoryData.error.message}</div>
      ) : (
        <>
          <div className="flex items-center py-4">
            <Input
              placeholder="Filter categories..."
              value={
                (table.getColumn("name")?.getFilterValue() as string) ?? ""
              }
              onChange={(event) =>
                table.getColumn("name")?.setFilterValue(event.target.value)
              }
              className="max-w-sm"
            />
          </div>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                      <TableCell>
                        <RoleGuard requiredRoles={["company-admin", "super-admin"]}>
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="outline"
                              onClick={() => handleEditClick(row.original)}
                              className="border-none"
                            >
                              <Edit className="h-5 w-5 " />
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => handleDeleteClick(row.original)}
                              className="border-none"
                            >
                              <Trash className="h-5 w-5" />
                            </Button>
                          </div>
                        </RoleGuard>
                        {/* <DropdownMenu>
                          <DropdownMenuTrigger>
                            <Button variant="outline">
                              <DotsHorizontalIcon className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem
                              onClick={() => handleEditClick(row.original)}
                            >
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDeleteClick(row.original)}
                            >
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu> */}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      No results.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <div className="flex items-center justify-end space-x-2 py-4">
            <div className="flex-1 text-sm text-muted-foreground">
              {table.getFilteredSelectedRowModel().rows.length} of{" "}
              {table.getFilteredRowModel().rows.length} account(s) selected.
            </div>
            <div className="space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}

      {isEditModalOpen && (
        <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
          <DialogOverlay />
          <DialogContent>
            <DialogTitle>Edit Category</DialogTitle>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  name="name"
                  value={currentCategory?.name || ""}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  name="description"
                  type="description"
                  value={currentCategory?.description || ""}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="flex justify-center w-full">
                <Button type="submit" className="w-full">
                  Update Category
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {isDeleteModalOpen && (
        <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
          <DialogOverlay />
          <DialogContent>
            <DialogTitle>Confirm Delete</DialogTitle>
            <p>Are you sure you want to delete this category?</p>
            <div className="flex justify-end gap-2 w-full mt-4">
              <Button
                variant="outline"
                onClick={() => setIsDeleteModalOpen(false)}
                className="mr-2"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  if (categoryToDelete) {
                    deleteMutation.mutate(categoryToDelete._id);
                    setIsDeleteModalOpen(false);
                  }
                }}
                // className="w-full"
              >
                Delete 
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

const CategoriesPage = withAuthorization(CategoriesTable, [
  Role.SuperAdmin,
  Role.CompanyAdmin,
  Role.Employee,
]);

export default CategoriesPage;
